import { useState } from 'react'
import { useNavigate } from 'react-router'
import dayjs from 'dayjs'
import range from 'lodash/range'
import { useDebounce } from 'usehooks-ts'

import MoreOutlinedIcon from '@ant-design/icons/MoreOutlined'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import Popover from '@mui/material/Popover'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@components/_mui/Typography'

import SearchInput from '@components/SearchInput'

import { useMentions } from './Mentions.hooks'

export default function MentionsPopover({ anchorEl, open = false, onClose }) {
  const [type, setType] = useState('received')
  const [search, setSearch] = useState('')
  const [unreplied, setUnreplied] = useState(true)

  const debouncedSearch = useDebounce(search, 500)

  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } = useMentions({
    type,
    unreplied: unreplied || undefined,
    mentioned_user_name: debouncedSearch,
  })

  const showLoading = isPending
  const showEmpty = !showLoading && data?.length === 0
  const showData = !showEmpty && data?.length > 0

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      slotProps={{
        paper: {
          sx: { width: '440px' },
          'data-testid': 'mentions-popover',
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', px: 3, py: 2 }}>
        <Typography variant="h4" sx={{ flex: '1 1 auto' }}>
          Mentions
        </Typography>
        <FormControlLabel
          label="Only show unreplied"
          value="start"
          labelPlacement="start"
          control={<Switch checked={unreplied} onChange={(e, checked) => setUnreplied(checked)} />}
        />
      </Stack>
      <Tabs value={type} onChange={(e, tab) => setType(tab)} sx={{ px: 2 }}>
        <Tab label="Incoming" value="received" data-testid="mentions-received-tab" />
        <Tab label="Outgoing" value="sent" data-testid="mentions-sent-tab" />
      </Tabs>
      <Divider />
      {type === 'sent' && (
        <>
          <Box sx={{ p: 1 }}>
            <SearchInput
              id="user-search"
              label="Filter by Recipient"
              placeholder="Enter recipient's name"
              value={search}
              onChange={setSearch}
            />
          </Box>
          <Divider />
        </>
      )}
      {showLoading && (
        <Content>
          {range(0, 4).map((item, i, data) => (
            <MentionItem.Loading key={item} divider={item < data.length - 1} />
          ))}
        </Content>
      )}
      {showEmpty && (
        <Box sx={{ p: 2 }} data-testid="mentions-empty">
          <Typography variant="h5" align="center" sx={{ color: 'text.secondary', height: 50, fontWeight: 'normal' }}>
            Nothing to see here!
          </Typography>
        </Box>
      )}
      {showData && (
        <Content>
          {data.map((mention, i) => (
            <MentionItem key={mention.id} divider={i < data.length - 1} type={type} mention={mention} onClose={onClose} />
          ))}
          {hasNextPage && (
            <Button
              fullWidth
              loading={isFetchingNextPage}
              onClick={() => fetchNextPage()}
              endIcon={<MoreOutlinedIcon rotate={90} />}
              loadingPosition="end"
              sx={{ textTransform: 'none' }}
              data-testid="mentions-more"
            >
              more
            </Button>
          )}
        </Content>
      )}
    </Popover>
  )
}

function Content({ children }) {
  return (
    <Box sx={{ maxHeight: '360px', overflowY: 'auto' }}>
      <List disablePadding data-testid="mentions-list">
        {children}
      </List>
    </Box>
  )
}

function MentionItem({ divider, type, mention, onClose }) {
  const navigate = useNavigate()
  const testId = `mention-item-${mention.id}`

  return (
    <ListItemButton
      divider={divider}
      onClick={() => {
        navigate(`/app/patients/${mention.patientId}/chat?messageId=${mention.replyMessageId}`)
        onClose()
      }}
      sx={{ transition: 'opacity 0.4s ease-in-out', opacity: mention.repliedAt ? 0.5 : 1 }}
      data-testid={`mention-item-${mention.id}`}
    >
      {type === 'received' && (
        <div>
          <Typography variant="body2" data-testid={`${testId}-content`}>
            <b>{mention.mentionee?.fullName || 'User'}</b> mentioned you in the patient's internal chat (MRN&nbsp;{mention.patientId})
          </Typography>
          {mention.createdAt && (
            <Typography variant="caption" data-testid={`${testId}-date`} sx={{ color: 'text.secondary' }}>
              {dayjs(mention.createdAt).format('MMM D, hh:mm A')}
            </Typography>
          )}
        </div>
      )}
      {type === 'sent' && (
        <div>
          <Typography variant="body2" data-testid={`${testId}-content`}>
            You mentioned <b>{mention.mentioned?.fullName || 'User'}</b> in the patient's internal chat (MRN&nbsp;{mention.patientId})
          </Typography>
          {mention.createdAt && (
            <Typography variant="caption" data-testid={`${testId}-date`} sx={{ color: 'text.secondary' }}>
              {dayjs(mention.createdAt).format('MMM D, hh:mm A')}
            </Typography>
          )}
        </div>
      )}
    </ListItemButton>
  )
}

MentionItem.Loading = function ({ divider }) {
  return (
    <ListItemButton divider={divider}>
      <Box sx={{ width: '100%' }}>
        <Typography variant="body2">
          <Skeleton />
          <Skeleton width={150} />
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          <Skeleton width={100} />
        </Typography>
      </Box>
    </ListItemButton>
  )
}
